import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { BarChart2, RefreshCw, Calendar } from 'lucide-react';
import Header from '../../components/ui/Header';
import SummaryStatCards from './components/SummaryStatCards';
import EngagementByLevelChart from './components/EngagementByLevelChart';
import HeatMapGrid from './components/HeatMapGrid';
import CoverageGapsTable from './components/CoverageGapsTable';
import TrendingChart from './components/TrendingChart';
import TopBottomPerformers from './components/TopBottomPerformers';
import VideoPerformanceTable from './components/VideoPerformanceTable';
import {
  getVideoEngagementSummary,
  getEngagementByLevel,
  getHeatMapData,
  getCoverageGaps,
  getTrendingData,
  getTopBottomPerformers,
  getVideoPerformanceTable,
} from '../../services/videoEngagementAnalyticsService';

const VideoEngagementAnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [levelData, setLevelData] = useState([]);
  const [heatMapData, setHeatMapData] = useState([]);
  const [coverageGaps, setCoverageGaps] = useState([]);
  const [trendingData, setTrendingData] = useState([]);
  const [performers, setPerformers] = useState(null);
  const [videoTable, setVideoTable] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, levelRes, heatMapRes, coverageRes, trendingRes, performersRes, tableRes] = await Promise.all([
        getVideoEngagementSummary(dateRange),
        getEngagementByLevel(dateRange),
        getHeatMapData(dateRange),
        getCoverageGaps(),
        getTrendingData(dateRange),
        getTopBottomPerformers(dateRange),
        getVideoPerformanceTable(dateRange),
      ]);

      if (summaryRes?.error) throw summaryRes?.error;
      if (levelRes?.error) throw levelRes?.error;
      if (heatMapRes?.error) throw heatMapRes?.error;
      if (coverageRes?.error) throw coverageRes?.error;
      if (trendingRes?.error) throw trendingRes?.error;
      if (performersRes?.error) throw performersRes?.error;
      if (tableRes?.error) throw tableRes?.error;

      setSummary(summaryRes?.data);
      setLevelData(levelRes?.data || []);
      setHeatMapData(heatMapRes?.data || []);
      setCoverageGaps(coverageRes?.data || []);
      setTrendingData(trendingRes?.data || []);
      setPerformers(performersRes?.data);
      setVideoTable(tableRes?.data || []);
    } catch (err) {
      console.error('Error fetching engagement analytics:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Video Engagement Analytics | RoleCall</title>
        <meta name="description" content="Track video completion rates, engagement metrics, coverage gaps, and trending analysis per song level" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-xl">
                <BarChart2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Video Engagement Analytics</h1>
                <p className="text-purple-200 text-sm mt-0.5">
                  Completion rates, engagement metrics, coverage gaps &amp; trending analysis
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Date range filter */}
              <div className="flex items-center gap-1 bg-white/10 rounded-xl p-1">
                <Calendar className="w-4 h-4 text-purple-300 ml-2" />
                {[7, 30, 90]?.map(days => (
                  <button
                    key={days}
                    onClick={() => setDateRange(days)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      dateRange === days
                        ? 'bg-white text-purple-900 shadow'
                        : 'text-purple-200 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>

              {/* Refresh */}
              <button
                onClick={fetchAllData}
                disabled={loading}
                className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-5 mb-6">
              <p className="text-red-200 font-medium">Error loading analytics</p>
              <p className="text-red-300 text-sm mt-1">{error}</p>
              <button
                onClick={fetchAllData}
                className="mt-3 px-4 py-2 bg-red-500/30 hover:bg-red-500/50 text-white text-sm rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* 1. Engagement Metrics Cards */}
          <section className="mb-8">
            <div className="mb-4">
              <h2 className="text-white font-bold text-lg">Engagement Metrics</h2>
              <p className="text-purple-300 text-sm mt-0.5">
                Average watch time, total watch events, replay rate, and drop-off point per level
              </p>
            </div>
            <SummaryStatCards stats={summary} levelData={levelData} loading={loading} />
          </section>

          {/* 2. Heat Map Grid */}
          <section className="mb-8">
            <div className="mb-4">
              <h2 className="text-white font-bold text-lg">Completion Rate Heat Map</h2>
              <p className="text-purple-300 text-sm mt-0.5">
                Songs as rows, levels as columns — cell color intensity based on completion rate
              </p>
            </div>
            <HeatMapGrid data={heatMapData} loading={loading} />
          </section>

          {/* 3. Trending Analysis */}
          <section className="mb-8">
            <TrendingChart data={trendingData} loading={loading} dateRange={dateRange} />
          </section>

          {/* 4. Top & Bottom Performers by Level */}
          <section className="mb-8">
            <div className="mb-4">
              <h2 className="text-white font-bold text-lg">Level Performance Rankings</h2>
              <p className="text-purple-300 text-sm mt-0.5">
                Top and bottom performing levels by average completion rate
              </p>
            </div>
            <TopBottomPerformers data={performers} loading={loading} />
          </section>

          {/* 5. Engagement by Level Chart */}
          <section className="mb-8">
            <EngagementByLevelChart data={levelData} loading={loading} />
          </section>

          {/* 6. Coverage Gaps */}
          <section className="mb-8">
            <div className="mb-4">
              <h2 className="text-white font-bold text-lg">Coverage Gaps</h2>
              <p className="text-purple-300 text-sm mt-0.5">
                Songs missing videos for specific levels — sortable by coverage count
              </p>
            </div>
            <CoverageGapsTable data={coverageGaps} loading={loading} />
          </section>

          {/* 7. Video Performance Table */}
          <section className="mb-8">
            <VideoPerformanceTable data={videoTable} loading={loading} />
          </section>
        </main>
      </div>
    </>
  );
};

export default VideoEngagementAnalyticsDashboard;
